import React, { useState, useEffect, useId } from "react";
import Select from "react-select";
import styles from "../../../../ManagerDashboard/Dashboard.module.scss";
import "accountOwner/pages/Scheduler/components/SchedulerFilters/SchedulerFilters.scss";
import SearchIcon from "../../../../../../assets/images/search-icon.svg";
import FilterDefaultIcon from "../../../../../../assets/images/filter-icon-default.svg";
import FilterIcon from "../../../../../../assets/images/clicked.svg";
import Text from "components/Text";
import { withTranslation } from "react-i18next";
import { Spinner } from "reactstrap";

function OfficeRoleShedulingFilter({
  t,
  rolesData = [],
  selectedRoleOption,
  handleRoleChange,
  officeList = [],
  selectedOfficeOption,
  handleOfficeChange,
  staffData = [],
  staffLoading = false,
  loadingRoleData = false,
  search = "",
  onSearch = () => {},
  eventOrShiftTab = false,
  selectedStaff = [],
  setSelectedStaff = () => {},
}) {
  const [selectAll, setSelectAll] = useState(false);
  const [selectDropdown, setSelectDropdown] = useState(false);
  // Unique per mounted instance so checkbox ids stay collision-free.
  const instanceId = useId();

  // Check if this specific member is selected
  const isSelected = (member) => {
    return selectedStaff.some(
      (item) =>
        item.userId === (member.user?.id || member.userId) &&
        (item.officeId === undefined || item.officeId === member.officeId),
    );
  };

  // Should disable a member if it's not selected AND another member with same userId is selected
  const shouldDisableMember = (member) => {
    if (eventOrShiftTab && selectedStaff.length) {
      return selectedStaff[0]?.officeId !== member?.officeId;
    }
    // If this specific member is already selected, never disable it
    if (isSelected(member)) {
      return false;
    }
    // Get the userId of this member
    const userId = member.user?.id || member.userId;
    if (!userId) return false;
    // Check if any other member with the same userId is already selected
    return selectedStaff.some((item) => {
      const itemUserId = item.user?.id || item.userId;
      return itemUserId === userId;
    });
  };

  const handleChange = (e, item) => {
    if (e.target.checked) {
      // Only allow selection if not disabled
      if (!shouldDisableMember(item)) {
        setSelectedStaff([
          ...selectedStaff,
          {
            ...item,
            userId: item.user?.id || item.userId,
          },
        ]);
      }
    } else {
      setSelectedStaff(
        selectedStaff.filter(
          (staff) => staff.userId !== (item?.user?.id || item.userId),
        ),
      );
    }
  };

  // Helper to get the officeId to use for Shift mode
  const getShiftOfficeId = () => {
    if (eventOrShiftTab) {
      // If any staff are selected, use their officeId
      if (selectedStaff.length > 0) {
        return selectedStaff[0]?.officeId;
      }
      // Otherwise, use the first visible staff's officeId
      const firstSelectable = staffData.find(
        (member) => !shouldDisableMember(member),
      );
      return firstSelectable?.officeId;
    }
    return undefined;
  };

  const handleAllChange = (checked) => {
    if (checked) {
      // For Shift: only select staff from the same office
      let newSelected = [...selectedStaff];
      const selectedIds = new Set(selectedStaff.map((item) => item.id));
      const userIdsSelected = new Set(
        selectedStaff.map((item) => item?.userId || item?.user?.id),
      );
      let selectableStaff = staffData.filter(
        (member) => !shouldDisableMember(member),
      );
      if (eventOrShiftTab) {
        const officeId = getShiftOfficeId();
        selectableStaff = selectableStaff.filter(
          (member) => member.officeId === officeId,
        );
      }
      selectableStaff.forEach((member) => {
        const userId = member.user?.id || member.userId;
        if (userIdsSelected.has(userId)) return;
        if (selectedIds.has(member.id)) return;
        newSelected.push({ ...member, userId });
        userIdsSelected.add(userId);
      });
      setSelectedStaff(newSelected);
    } else {
      // Deselect all visible (filtered) staff
      let selectableStaff = staffData.filter(
        (member) => !shouldDisableMember(member),
      );
      if (eventOrShiftTab) {
        const officeId = getShiftOfficeId();
        selectableStaff = selectableStaff.filter(
          (member) => member.officeId === officeId,
        );
      }
      const visibleIds = new Set(selectableStaff.map((member) => member.id));
      setSelectedStaff((prev) =>
        prev.filter((item) => !visibleIds.has(item.id)),
      );
    }
  };

  useEffect(() => {
    // Only consider visible, not-disabled staff
    let selectableStaff = staffData.filter(
      (member) => !shouldDisableMember(member),
    );
    if (eventOrShiftTab) {
      const officeId = getShiftOfficeId();
      selectableStaff = selectableStaff.filter(
        (member) => member.officeId === officeId,
      );
    }
    if (
      selectableStaff.length > 0 &&
      selectableStaff.every((member) => isSelected(member))
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedStaff, staffData, eventOrShiftTab]);

  const renderFilters = () => (
    <>
      <div className={styles.filter_search_icon_wrapper}>
        <div className={styles.new_search_bar}>
          <span className={styles.search_icon}>
            <img src={SearchIcon} alt="search icon" />
          </span>
          <input
            className={styles.search_input}
            type="text"
            placeholder="Search by Name"
            onChange={onSearch}
            value={search}
          />
        </div>
        <div onClick={() => setSelectDropdown(!selectDropdown)}>
          <img
            src={
              selectedRoleOption?.value || selectedOfficeOption?.value
                ? FilterDefaultIcon
                : FilterIcon
            }
            alt="Filter Icon"
            className={
              selectedRoleOption?.value || selectedOfficeOption?.value
                ? styles.filter_icon_default
                : styles.filter_icon
            }
          />
        </div>
      </div>
      {selectDropdown && (
        <div
          className={`${styles.select_container_new} member-filter review-rating-filter all-option custom-scroll employees-filter-dual-select-panel d-flex`}
        >
          <Select
            className={`${styles.react_select_box} react-select-container`}
            classNamePrefix="react-select"
            options={officeList}
            value={selectedOfficeOption}
            onChange={handleOfficeChange}
            isSearchable={false}
          />
          <Select
            className={`${styles.react_select_box} react-select-container`}
            classNamePrefix="react-select"
            options={rolesData}
            value={selectedRoleOption}
            onChange={handleRoleChange}
            isSearchable={false}
          />
        </div>
      )}
    </>
  );

  const renderStaffList = () => (
    <>
      {staffData.map((item, key) => {
        const checkboxId = `${instanceId}-staff-${item?.user?.id ?? item?.userId ?? key}-${item?.officeId ?? ""}`;

        return (
          <div
            className={`${styles["checkbox_wrapper_filter"]} ${
              shouldDisableMember(item) ? styles.disabled_list : ""
            }`}
            key={key}
          >
            <div
              className={`${styles["check_box_width"]} ch-checkbox c-field all-event-checkbox m-0 `}
            >
              <label>
                <input
                  type="checkbox"
                  id={checkboxId}
                  onChange={(e) => handleChange(e, item)}
                  checked={isSelected(item)}
                  disabled={shouldDisableMember(item)}
                />
                <span className={styles["checkbox_height"]}></span>
              </label>
            </div>
            <div
              className={`${styles["name_office"]} ${styles["clickable_name_office"]}`}
            >
              <label className="m-0" htmlFor={checkboxId}>
                <Text
                  size="14px"
                  weight="500"
                  color="#102C42"
                  lineHeight="16px"
                >
                  {`${item.user.firstName} ${item.user.lastName}`}
                </Text>
                <Text size="12px" weight="400" color="#87928D">
                  {`${
                    item?.designation?.name
                      ? item?.designation?.name + ", "
                      : ""
                  }${item?.office?.name || ""}`}
                </Text>
              </label>
            </div>
          </div>
        );
      })}
    </>
  );

  const renderLoaderAndStaffList = () => (
    <>
      {staffLoading || loadingRoleData ? (
        <div style={{ padding: "1rem", textAlign: "center" }}>
          <Spinner animation="border" size="sm" className="loader-spinner" />
        </div>
      ) : (
        <ul className="filter-list">
          {staffData.length > 0 ? (
            <ul>
              <li>
                <div className="ch-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      onChange={(e) => handleAllChange(e.target.checked)}
                      checked={selectAll}
                    />
                    <span>{t("staff.selectAll")}</span>
                  </label>
                </div>
              </li>
              <hr className="mt-1 mb-2" />
            </ul>
          ) : (
            <span>No staff found.</span>
          )}
          {renderStaffList()}
        </ul>
      )}
    </>
  );

  return (
    <div className={styles.flex_col_div}>
      {renderFilters()}
      {renderLoaderAndStaffList()}
    </div>
  );
}

export default withTranslation()(OfficeRoleShedulingFilter);
